import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { LOGO_URL } from "@/lib/brand";
import api from "@/lib/api";

function formatError(detail) {
  if (!detail) return "Erro ao processar a solicitação.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e.msg || JSON.stringify(e)).join(" ");
  if (detail.msg) return detail.msg;
  return String(detail);
}

export default function Login() {
  const { login, loginBase } = useAuth();
  const nav = useNavigate();
  const [base, setBase] = useState("");
  const [baseInfo, setBaseInfo] = useState(null);   // validated base details
  const [baseChecking, setBaseChecking] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPass, setShowPass] = useState(false);
  const [captchaOk, setCaptchaOk] = useState(false);
  const [busy, setBusy] = useState(false);

  const checkBase = async (raw) => {
    const slug = (raw || "").trim().toLowerCase();
    setBase(slug);
    setBaseInfo(null);
    if (slug.length < 3) return;
    setBaseChecking(true);
    try {
      const { data } = await api.get(`/auth/base/${encodeURIComponent(slug)}`);
      setBaseInfo(data);
    } catch {
      setBaseInfo(null);
    } finally {
      setBaseChecking(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!captchaOk) {
      toast.error("Confirme que você não é um robô.");
      return;
    }
    setBusy(true);
    try {
      if (base) {
        await loginBase(base, email, password);
      } else {
        // Legacy login (no base) — only works for admin Sollute / pre-tenant users.
        await login(email, password);
      }
      toast.success("Bem-vindo de volta!");
      nav("/app");
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail) || err.message);
      // Reset captcha after a failed attempt so retries don't bypass the gate.
      setCaptchaOk(false);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center p-4 sm:p-8"
      style={{
        background:
          "radial-gradient(900px 600px at 0% 0%, rgba(0,112,64,0.10), transparent 55%)," +
          "radial-gradient(900px 600px at 100% 100%, rgba(0,209,160,0.10), transparent 55%)," +
          "#F7FAFC",
      }}
    >
      <div
        className="w-full max-w-md bg-white rounded-3xl p-8 sm:p-10 so-fade-in"
        style={{ boxShadow: "0 30px 60px -20px rgba(0, 112, 64, 0.18), 0 8px 30px rgba(15,23,42,0.06)" }}
        data-testid="login-card"
      >
        {/* Brand — only Sollute logo, no extra decorative icons */}
        <div className="mb-8 so-logo-halo">
          <img src={LOGO_URL} alt="Sollute Ocupacional" className="h-14 w-auto" />
        </div>

        <form onSubmit={submit} data-testid="login-form" className="space-y-5">
          <div>
            <h1 className="text-xl font-bold text-slate-900 mb-1">Informe seus dados abaixo:</h1>
            <p className="text-xs text-slate-500">Plataforma NR-01 · Riscos Psicossociais</p>
          </div>

          {/* Cliente (Base) */}
          <div>
            <label className="label">Cliente (Base)</label>
            <div className="relative">
              <input
                type="text"
                placeholder="ex.: sua-empresa"
                value={base}
                onChange={(e) => checkBase(e.target.value)}
                autoComplete="organization"
                className="input pr-10"
                data-testid="login-base-input"
              />
              {baseChecking && (
                <i className="fa-solid fa-circle-notch fa-spin absolute right-3 top-1/2 -translate-y-1/2 text-slate-400" />
              )}
              {!baseChecking && baseInfo && (
                <i
                  className="fa-solid fa-circle-check absolute right-3 top-1/2 -translate-y-1/2"
                  style={{ color: "var(--so-primary)" }}
                  data-testid="login-base-valid"
                />
              )}
              {!baseChecking && base.length >= 3 && !baseInfo && (
                <i
                  className="fa-solid fa-circle-xmark absolute right-3 top-1/2 -translate-y-1/2 text-red-400"
                  data-testid="login-base-invalid"
                />
              )}
            </div>
            {baseInfo && (
              <div className="mt-2 text-xs flex items-center gap-2 text-slate-600" data-testid="login-base-confirmed">
                <i className="fa-solid fa-building" style={{ color: "var(--so-primary)" }}></i>
                Acessando <strong className="text-slate-900">{baseInfo.razao_social}</strong>
              </div>
            )}
          </div>

          {/* Usuário (email) */}
          <div>
            <label className="label">Usuário</label>
            <input
              type="email"
              required
              placeholder="seu e-mail"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="email"
              className="input"
              data-testid="login-email-input"
            />
          </div>

          {/* Senha */}
          <div>
            <label className="label">Senha</label>
            <div className="relative">
              <input
                type={showPass ? "text" : "password"}
                required
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="current-password"
                className="input pr-12"
                data-testid="login-password-input"
              />
              <button
                type="button"
                onClick={() => setShowPass((s) => !s)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-700 p-1"
                aria-label="Mostrar/ocultar senha"
                data-testid="login-toggle-password"
              >
                <i className={`fa-solid ${showPass ? "fa-eye-slash" : "fa-eye"}`}></i>
              </button>
            </div>
          </div>

          <div>
            <Link
              to="/recuperar-senha"
              className="text-sm font-medium hover:underline"
              style={{ color: "var(--so-primary)" }}
              data-testid="login-forgot-link"
            >
              Esqueci a senha
            </Link>
          </div>

          {/* reCAPTCHA placeholder (visual only — substituir por reCAPTCHA real no deploy) */}
          <FakeCaptcha checked={captchaOk} onChange={setCaptchaOk} />

          <button
            type="submit"
            disabled={busy}
            className="btn btn-primary w-full h-[52px] text-[15px]"
            data-testid="login-submit-button"
          >
            {busy ? (
              <><i className="fa-solid fa-circle-notch fa-spin"></i> Entrando…</>
            ) : (
              <>Acessar <i className="fa-solid fa-arrow-right"></i></>
            )}
          </button>

          <div className="text-center pt-4 border-t border-slate-100 text-sm text-slate-600">
            Não tem uma conta?{" "}
            <Link
              to="/register"
              className="font-semibold hover:underline"
              style={{ color: "var(--so-primary)" }}
              data-testid="login-register-link"
            >
              Experimente grátis
            </Link>
          </div>
        </form>
      </div>
    </div>
  );
}

function FakeCaptcha({ checked, onChange }) {
  const [verifying, setVerifying] = useState(false);
  const toggle = () => {
    if (checked) { onChange(false); return; }
    setVerifying(true);
    setTimeout(() => { setVerifying(false); onChange(true); }, 500);
  };
  return (
    <div
      className="border border-slate-200 rounded-md p-4 flex items-center gap-3 bg-slate-50"
      data-testid="login-captcha"
    >
      <button
        type="button"
        onClick={toggle}
        disabled={verifying}
        className={`w-7 h-7 border-2 rounded-sm flex items-center justify-center transition-all ${
          checked ? "" : "border-slate-400 bg-white hover:border-slate-600"
        }`}
        style={checked ? { background: "var(--so-primary)", borderColor: "var(--so-primary)" } : {}}
        aria-label="Não sou um robô"
        data-testid="login-captcha-checkbox"
      >
        {verifying ? (
          <i className="fa-solid fa-circle-notch fa-spin text-slate-500 text-xs"></i>
        ) : checked ? (
          <i className="fa-solid fa-check text-white text-sm"></i>
        ) : null}
      </button>
      <div className="flex-1 text-sm text-slate-700">Não sou um robô</div>
      <div className="text-right shrink-0">
        <div className="text-[10px] text-slate-400 leading-tight">reCAPTCHA</div>
        <div className="text-[9px] text-slate-400 leading-tight">Privacidade · Termos</div>
      </div>
    </div>
  );
}
